'use client';

import { useEffect, useState } from 'react';

const PAGE_HEADER_SELECTOR = '#TopNav-mainNav';
const VISIBLE_HEADER_SELECTOR = '.Navigation-lg';
const JUMP_LINK_SELECTOR = '[data-jump-link-sticky-tab]';

export function measureStickyHeaderOffset(): number {
	const pageHeader = document.querySelector<HTMLElement>(PAGE_HEADER_SELECTOR);
	const visibleHeader =
		pageHeader?.querySelector<HTMLElement>(VISIBLE_HEADER_SELECTOR) ?? pageHeader;
	const visibleHeaderBottom = visibleHeader?.getBoundingClientRect().bottom ?? 0;
	const outerHeaderBottom = pageHeader?.getBoundingClientRect().bottom ?? 0;
	const headerBottom =
		visibleHeaderBottom > 0 ? visibleHeaderBottom : Math.max(0, outerHeaderBottom);
	const jumpLink = document.querySelector<HTMLElement>(JUMP_LINK_SELECTOR);

	if (!jumpLink || window.getComputedStyle(jumpLink).position !== 'fixed') return headerBottom;

	const jumpLinkStyles = window.getComputedStyle(jumpLink);
	if (jumpLinkStyles.visibility === 'hidden' || jumpLinkStyles.display === 'none')
		return headerBottom;

	return Math.max(headerBottom, jumpLink.getBoundingClientRect().bottom);
}

export function useStickyHeaderOffset(): number {
	const [offset, setOffset] = useState(0);

	useEffect(() => {
		let frame: number | null = null;
		const updateOffset = () => {
			frame = null;
			const nextOffset = measureStickyHeaderOffset();
			setOffset(currentOffset => (currentOffset === nextOffset ? currentOffset : nextOffset));
		};
		const scheduleUpdate = () => {
			if (frame === null) frame = window.requestAnimationFrame(updateOffset);
		};

		updateOffset();
		window.addEventListener('scroll', scheduleUpdate, { passive: true });
		window.addEventListener('resize', scheduleUpdate);

		const pageHeader = document.querySelector<HTMLElement>(PAGE_HEADER_SELECTOR);
		const jumpLink = document.querySelector<HTMLElement>(JUMP_LINK_SELECTOR);
		const mutationObserver =
			typeof MutationObserver === 'undefined' ? null : new MutationObserver(scheduleUpdate);
		if (jumpLink) mutationObserver?.observe(jumpLink, { attributes: true });

		const resizeObserver =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
		if (pageHeader) resizeObserver?.observe(pageHeader);
		if (jumpLink) resizeObserver?.observe(jumpLink);

		return () => {
			window.removeEventListener('scroll', scheduleUpdate);
			window.removeEventListener('resize', scheduleUpdate);
			mutationObserver?.disconnect();
			resizeObserver?.disconnect();
			if (frame !== null) window.cancelAnimationFrame(frame);
		};
	}, []);

	return offset;
}
